import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from 'recharts';
import Button from '../UI/Button';

// Product-specific timeline content
const timelineContent = {
  nad: {
    title: "Your NAD+ Transformation Timeline",
    productName: "NAD+",
    subtitle: "Real results you can expect",
    yAxisLabel: "Energy & Cellular Health",
    withLabel: "With NAD+",
    withoutLabel: "Without NAD+",
    week1: "NAD+ levels begin to restore",
    week4: "Energy production increases dramatically",
    week9: "Cells become age-defying powerhouses"
  },
  glutathione: {
    title: "Your Glutathione Transformation Timeline",
    productName: "Glutathione",
    subtitle: "Real results you can expect",
    yAxisLabel: "Skin Health & Vitality",
    withLabel: "With Glutathione",
    withoutLabel: "Without Glutathione",
    week1: "Antioxidant protection begins",
    week4: "Skin brightening becomes visible",
    week9: "Full-body detox and radiant glow achieved"
  },
  vitaminb12: {
    title: "Your B12 Transformation Timeline",
    productName: "B12",
    subtitle: "Real results you can expect",
    yAxisLabel: "Energy & Mental Clarity",
    withLabel: "With B12",
    withoutLabel: "Without B12",
    week1: "Energy levels start to stabilize",
    week4: "Mental clarity and focus peak",
    week9: "Sustained all-day energy is your new normal"
  },
  lcarnitine: {
    title: "Your L-Carnitine Transformation Timeline",
    productName: "L-Carnitine",
    subtitle: "Real results you can expect",
    yAxisLabel: "Performance & Metabolism",
    withLabel: "With L-Carnitine",
    withoutLabel: "Without L-Carnitine",
    week1: "Fat metabolism activation begins",
    week4: "Performance improvements become noticeable",
    week9: "Peak fat-burning and athletic performance"
  }
};

export default function TransformationTimelineScreen({ product, onContinue }) {
  const content = timelineContent[product?.id] || timelineContent.nad;

  // Chart data showing WITH vs WITHOUT treatment
  // WITH line shows exponential growth reflecting titration protocol (low start, faster increase toward end)
  // NAD+ has more exponential curve (slower initial restoration)
  // Both lines start at the same point (20)
  const chartData = product?.id === 'nad'
    ? [
        {
          week: 0,
          withTreatment: 20,
          withoutTreatment: 20,
          label: 'Week 0'
        },
        {
          week: 4,
          withTreatment: 40,
          withoutTreatment: 15,
          label: 'Week 4'
        },
        {
          week: 12,
          withTreatment: 95,
          withoutTreatment: 10,
          label: 'Week 12'
        }
      ]
    : [
        {
          week: 0,
          withTreatment: 20,
          withoutTreatment: 20,
          label: 'Week 0'
        },
        {
          week: 4,
          withTreatment: 45,
          withoutTreatment: 15,
          label: 'Week 4'
        },
        {
          week: 12,
          withTreatment: 95,
          withoutTreatment: 10,
          label: 'Week 12'
        }
      ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="max-w-2xl mx-auto"
    >
      <div className="bg-white rounded-luxury shadow-luxury p-4 md:p-6 lg:p-8">
        {/* Title */}
        <h2 className="text-3xl md:text-4xl font-bold text-charcoal text-center mb-3">
          Your <span className="text-renuvia-gold">{content.productName}</span> Transformation Timeline
        </h2>

        <p className="text-lg text-center text-slate mb-8">
          {content.subtitle}
        </p>

        {/* Timeline Chart */}
        <div className="bg-warm-beige rounded-xl p-2 md:p-4 lg:p-6 mb-8">
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  label={{
                    value: content.yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: '#64748b', fontSize: 11, textAnchor: 'middle' }
                  }}
                  tick={{ fill: '#64748b', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                />
                {/* WITH Treatment Line - Brand Green, solid */}
                <Line
                  type="monotone"
                  dataKey="withTreatment"
                  stroke="#059669"
                  strokeWidth={3}
                  dot={{ fill: '#059669', r: 6 }}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationBegin={300}
                  name={content.withLabel}
                />
                {/* WITHOUT Treatment Line - Gray, dashed */}
                <Line
                  type="monotone"
                  dataKey="withoutTreatment"
                  stroke="#9ca3af"
                  strokeWidth={3}
                  strokeDasharray="8 8"
                  dot={{ fill: '#9ca3af', r: 6 }}
                  isAnimationActive={true}
                  animationDuration={1500}
                  animationBegin={600}
                  name={content.withoutLabel}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 rounded" style={{ backgroundColor: '#059669' }}></div>
              <span className="text-sm text-charcoal font-semibold">{content.withLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-1 bg-gray-400 rounded" style={{ backgroundImage: 'repeating-linear-gradient(to right, #9ca3af 0, #9ca3af 8px, transparent 8px, transparent 16px)' }}></div>
              <span className="text-sm text-slate font-medium">{content.withoutLabel}</span>
            </div>
          </div>

          {/* Timeline milestones */}
          <div className="space-y-3">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[105px]">• Week 1-4:</span>
              <span className="text-charcoal">{content.week1}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[105px]">• Week 4-8:</span>
              <span className="text-charcoal">{content.week4}</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
              className="flex items-start gap-3"
            >
              <span className="font-bold text-charcoal min-w-[105px]">• Week 9+:</span>
              <span className="text-charcoal">{content.week9}</span>
            </motion.div>
          </div>
        </div>

        {/* Trust message */}
        <div className="bg-gradient-to-r from-renuvia-gold/10 to-renuvia-gold/5 rounded-xl p-6 mb-8">
          <p className="text-center text-charcoal">
            Based on <span className="font-bold">clinical studies</span> and <span className="font-bold">200,000+ patient outcomes</span>
          </p>
        </div>

        <Button onClick={onContinue} fullWidth>
          See What Our Patients Say →
        </Button>
      </div>
    </motion.div>
  );
}
